/**
 * Types for lighting control systems
 */

import { v4 as uuidv4 } from 'uuid';
import { logger } from '@/lib/logger';

// Control type enum
export type ControlType = 'keypad' | 'dimmer' | 'switch' | 'auxiliary' | 'centralized-keypad';

// Action interface for button actions
export interface ControlAction {
  // ID of the action
  id: string;
  // Name of the action
  name: string;
  // Description of what the action does
  description?: string;
  // Circuit IDs affected by this action
  circuitIds?: string[];
  // Custom parameters for the action
  params?: Record<string, any>;
  // Action type
  actionType?: 'toggle' | 'dimUp' | 'dimDown' | 'scene' | 'custom';
}

// Base interface for lighting controls
export interface LightingControl {
  // Unique identifier
  id: string;
  // Display name
  name: string;
  // Type of control
  type: ControlType;
  // Text description of the assigned load or circuit
  assignedLoad?: string;
  // Reference to a circuit ID
  circuitId?: string;
  // Floor plan this control belongs to
  floorPlanId: string;
  // Number of buttons on the control
  numberOfButtons: number;
  // Labels for each button (length should match numberOfButtons)
  labels: string[];
  // Actions for each button (length should match numberOfButtons)
  actions: ControlAction[];
  // X coordinate on the floor plan
  xPosition: number;
  // Y coordinate on the floor plan
  yPosition: number;
  // Room ID where this control is located
  roomId?: string;
  // Creation timestamp
  createdAt?: string;
  // Last update timestamp
  updatedAt?: string;
  // Sequence number for ordered controls (e.g., centralized keypads)
  sequenceNumber?: number;
  // Product ID reference to product library
  productId?: string;
  // Scale factor for visual representation (default 1.0)
  scale?: number;
  // Show product image callout on floor plan
  showProductImage?: boolean;
  // Product image callout position X
  productImageX?: number;
  // Product image callout position Y
  productImageY?: number;
  // Product image callout width
  productImageWidth?: number;
  // Product image callout height
  productImageHeight?: number;
}

// Type for creating a new control
export type NewLightingControl = Omit<LightingControl, 'id' | 'createdAt' | 'updatedAt'>;

// Type for updating an existing control
export type UpdateLightingControl = Partial<
  Omit<LightingControl, 'id' | 'createdAt' | 'updatedAt'>
>;

// Helper function to create empty action objects
export function createEmptyActions(count: number): ControlAction[] {
  return Array(count)
    .fill(null)
    .map(() => ({
      id: uuidv4(),
      name: '',
      actionType: 'toggle',
    }));
}

// Helper function to ensure labels and actions match the number of buttons
export function validateControlData(control: NewLightingControl): NewLightingControl {
  const { numberOfButtons } = control;
  let { labels = [], actions = [] } = control;

  // Ensure labels is an array
  if (!Array.isArray(labels)) {
    logger.warn('Labels is not an array, creating empty array');
    labels = [];
  }

  // Ensure actions is an array
  if (!Array.isArray(actions)) {
    logger.warn('Actions is not an array, creating empty array');
    actions = [];
  }

  // Ensure labels match numberOfButtons
  if (labels.length !== numberOfButtons) {
    logger.log(`Adjusting labels array from ${labels.length} to ${numberOfButtons} items`);
    if (labels.length > numberOfButtons) {
      // Truncate if too many
      labels = labels.slice(0, numberOfButtons);
    } else {
      // Add empty labels if too few
      labels = [...labels, ...Array(numberOfButtons - labels.length).fill('')];
    }
  }

  // Ensure actions match numberOfButtons
  if (actions.length !== numberOfButtons) {
    logger.log(`Adjusting actions array from ${actions.length} to ${numberOfButtons} items`);
    if (actions.length > numberOfButtons) {
      // Truncate if too many
      actions = actions.slice(0, numberOfButtons);
    } else {
      // Create completely new actions array to avoid references
      const newActions = [];
      // Keep existing actions
      for (let i = 0; i < actions.length; i++) {
        newActions.push(actions[i]);
      }
      // Add new actions for the remaining buttons
      for (let i = actions.length; i < numberOfButtons; i++) {
        newActions.push({
          id: uuidv4(),
          name: '',
          actionType: 'toggle',
        });
      }
      actions = newActions;
    }
  }

  // Verify all actions have an ID
  actions = actions.map(action => {
    if (!action.id) {
      return { ...action, id: uuidv4() };
    }
    return action;
  });

  return {
    ...control,
    labels,
    actions,
  };
}
